import React, { memo } from "react";
import { motion } from "framer-motion";

// 다양한 애니메이션 variants 정의
const fadeInUpVariants = {
  hidden: { 
    opacity: 0, 
    y: -30,
    scale: 0.95 // 약간의 스케일 효과 추가
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1
  }
};

// 좌측에서 나타나는 애니메이션
const slideInFromLeftVariants = {
  hidden: {
    opacity: 0,
    x: -50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1
  }
};

// 우측에서 나타나는 애니메이션
const slideInFromRightVariants = {
  hidden: {
    opacity: 0,
    x: 50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1
  }
};

// 타이핑 효과 variants
const typingVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8
  },
  visible: {
    opacity: 1,
    scale: 1
  }
};

// 공통 transition 설정 (더 세밀한 제어)
const createTransition = (delay = 0, type = "spring") => ({
  type,
  duration: type === "spring" ? undefined : 0.7,
  damping: type === "spring" ? 25 : undefined,
  stiffness: type === "spring" ? 120 : undefined,
  delay,
  ease: type === "tween" ? [0.25, 0.1, 0.25, 1] : undefined // 커스텀 cubic-bezier
});

// 스프링 애니메이션을 위한 특별 transition
const springTransition = (delay = 0) => ({
  type: "spring",
  damping: 20,
  stiffness: 100,
  delay,
  mass: 1
});

// viewport 설정 최적화
const optimizedViewport = {
  once: true, // 한 번만 애니메이션 실행
  margin: "-15% 0px -15% 0px",
  amount: 0.3 // 30% 보일 때 트리거
};

// hover 애니메이션 variants
const hoverVariants = {
  hover: {
    scale: 1.05,
    transition: { duration: 0.2 }
  }
};

const ServiceContent = memo(() => {
  console.log('🎨 ServiceContent 렌더링');

  return (
    <div className="absolute inset-0 z-10 px-4 lg:px-12">
      <div className="absolute z-30 lg:left-[270px] w-full h-full flex flex-col items-baseline justify-start mt-32 lg:mt-56 transition-all duration-300">
        <div className="flex items-center mb-3 w-full">
          <motion.span 
            variants={slideInFromLeftVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            transition={springTransition(0.2)}
            viewport={optimizedViewport}
            className="relative text-4xl lg:text-5xl font-[TAEBAEKfont] drop-shadow-2xl text-white cursor-pointer
            after:block after:absolute after:top-[60%] after:-inset-1 after:-skew-y-2 after:bg-red-500 after:z-[-1] after:h-[20px]
            after:transition-all after:duration-300 hover:after:bg-red-400"
            style={{
              willChange: 'transform, opacity',
              backfaceVisibility: 'hidden'
            }}
            {...hoverVariants}
          >
            SERVICE
          </motion.span> 
        </div>
        
        <motion.div 
          variants={fadeInUpVariants}
          initial="hidden"
          whileInView="visible"
          transition={createTransition(0.4, "spring")}
          viewport={optimizedViewport}
          className="text-gray-200 text-xl lg:text-4xl overflow-hidden font-[SUIT-Regular]"
          style={{
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden'
          }}
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" }}
            viewport={optimizedViewport}
          >
            솔컴바인은 10년 이상의 노하우로
          </motion.span>
        </motion.div>
        
        <motion.div 
          variants={fadeInUpVariants}
          initial="hidden"
          whileInView="visible"
          transition={createTransition(0.7, "spring")}
          viewport={optimizedViewport}
          className="text-gray-200 text-xl lg:text-4xl mt-2 lg:mt-2 overflow-hidden font-[SUIT-Regular]"
          style={{
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden'
          }}
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6, ease: "easeOut" }}
            viewport={optimizedViewport}
          >
            기업의 변화를 함께 만들어가겠습니다.
          </motion.span>
        </motion.div>              
        
        <motion.div 
          variants={fadeInUpVariants}
          initial="hidden"
          whileInView="visible"
          transition={createTransition(1.0, "tween")}
          viewport={optimizedViewport}
          className="hidden lg:block text-white/60 text-xl lg:text-2xl mt-1 lg:mt-3 pt-8 overflow-hidden font-[SUIT-Regular]"
          style={{
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden'
          }}
        >
          <motion.span
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            viewport={optimizedViewport}
          >
            최고의 기술과 노하우로 비즈니스 변화를 이끌어가겠습니다.
          </motion.span>
        </motion.div>
      </div> 

      <div className="absolute z-20 top-[60%] lg:top-[70%] lg:left-[60%] w-full h-full flex flex-col items-baseline justify-start transition-all duration-500">
        <motion.div 
          variants={slideInFromRightVariants}
          initial="hidden"
          whileInView="visible"
          transition={springTransition(1.3)}
          viewport={optimizedViewport}
          className="text-gray-400 text-xl lg:text-4xl font-bold text-end text-white overflow-hidden"
          style={{
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden'
          }}
        >
          <motion.span
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.5, duration: 0.7, ease: "easeOut" }}
            viewport={optimizedViewport}
          >
            Cutting Edge Technology
          </motion.span>
        </motion.div>
        
        <motion.h2 
          variants={typingVariants}
          initial="hidden"
          whileInView="visible"
          whileHover={{ 
            scale: 1.02,
            textShadow: "0 0 20px rgba(239, 68, 68, 0.5)",
            transition: { duration: 0.3 }
          }}
          transition={createTransition(1.6, "spring")}
          viewport={optimizedViewport}
          className="text-red-600 text-2xl lg:text-5xl mt-2 lg:mt-2 font-bold drop-shadow-xl cursor-pointer
          transition-all duration-300 hover:text-red-500"
          style={{
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden'
          }}
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ 
              delay: 1.8, 
              duration: 0.8, 
              type: "spring",
              damping: 15,
              stiffness: 120
            }}
            viewport={optimizedViewport}
          >
            SOLCOMBINE
          </motion.span>
        </motion.h2>                             
      </div>  
    </div>
  );
});

// displayName 설정 (디버깅 시 유용)
ServiceContent.displayName = 'ServiceContent';

export default ServiceContent; 